// src/pages/TextCompare/TextCompare.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Split, Sparkles, AlertTriangle, FileText, Trash2, Download, Shield, Zap, Search, ArrowUp, ArrowDown } from 'lucide-react';

import SEO from '../../../components/SEO/SEO';
import { seoConfig } from '../../../config/seo';
import SecurityBadge from '../../../components/Shared/SecurityBadge/SecurityBadge';
import apiService from '../../../services/apiService';
// Modular Components
import InputSection from '../../../components/TextCompare/InputSection';
import DiffView from '../../../components/TextCompare/DiffView';
import ComparisonStats from '../../../components/TextCompare/ComparisonStats';
import SidebarTools from '../../../components/TextCompare/SidebarTools';
import ComparisonSummary from '../../../components/TextCompare/ComparisonSummary';
import SEOContent from '../../../components/TextCompare/SEOContent';
import { useTextComparison } from '../../../hooks/useTextComparison';

import {
    ToolPageLayout,
    ToolInfoStrip,
    ToolSection,
    ToolStepCard,
    ToolFeatureCard,
    ToolFaqCta,
    ToolRelatedCard,
} from '../components';

const TextCompare: React.FC = () => {
    // Use custom hook for business logic
    const {
        oldText, newText, fileName1, fileName2,
        comparison, matchPercentage, diffCount, stats, summary,
        isLoading, error, options, aiSettings, aiSummary, impactFlags, isAiLoading,
        activeTransforms,
        handleOldTextChange, handleNewTextChange,
        handleCompare, handleGenerateAiSummary, handleTransformText,
        updateOptions, handleClear, handleFileUpload, setAiShowSummary,
        setFileName1, setFileName2
    } = useTextComparison();

    // UI View State
    const [isCollapsed, setIsCollapsed] = useState(false);
    const sidebarInteractedRef = useRef(false);
    const resultsRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLDivElement>(null);
    const isManualCompareRef = useRef(false);

    const [activeSection, setActiveSection] = useState<'results-top' | 'editors' | 'between'>('results-top');
    const [showNavigator, setShowNavigator] = useState(false);

    // Navigation State

    const [scrollToIndex, setScrollToIndex] = useState<{ index: number | null, timestamp: number }>({ index: null, timestamp: 0 });

    // Export State
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [exportIncludeSummary, setExportIncludeSummary] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);

    // Sidebar Auto-Hide Logic
    useEffect(() => {
        // Only run auto-expand/collapse logic if triggered by a Manual Comparison Click
        if (comparison && isManualCompareRef.current) {
            setIsCollapsed(false);
            sidebarInteractedRef.current = false;


            setTimeout(() => {
                resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);

            const timer = setTimeout(() => {
                if (!sidebarInteractedRef.current) {
                    setIsCollapsed(true);
                }
            }, 2000);

            isManualCompareRef.current = false;
            return () => clearTimeout(timer);
        }
    }, [comparison]);

    // Navigator Visibility logic
    useEffect(() => {
        const handleScroll = () => {
            if (!comparison) {
                setShowNavigator(false);
                return;
            }

            const scrollY = window.scrollY;
            const windowHeight = window.innerHeight;
            const fullHeight = document.documentElement.scrollHeight;
            const resultsTop = resultsRef.current?.offsetTop || 0;
            const inputTop = inputRef.current?.offsetTop || 0;

            const isNearBottom = scrollY + windowHeight > fullHeight - 150;
            setShowNavigator(scrollY > 1200 && !isNearBottom);

            if (scrollY < resultsTop + 1200) {
                setActiveSection('results-top');
            } else if (scrollY > inputTop - 1200) {
                setActiveSection('editors');
            } else {
                setActiveSection('between');
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [comparison]);

    const handleManualToggle = (collapsedState: boolean) => {
        setIsCollapsed(collapsedState);
        sidebarInteractedRef.current = true;
    };

    const scrollToSection = (ref: React.RefObject<any>) => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };



    // Auto-Compare: Only trigger when Backend-Affecting options change
    useEffect(() => {
        if (oldText.trim() || newText.trim()) {
            const timer = setTimeout(handleCompare, 100);
            return () => clearTimeout(timer);
        }
    }, [
        options.ignoreCase,
        options.ignoreWhitespace,
        options.ignorePunctuation,
        options.characterLevel,
        options.detectMoves
    ]);

    // Live Sync: Trigger compare on Text Change if RealTime is enabled
    useEffect(() => {
        if (options.realTime && (oldText.trim() || newText.trim())) {
            const timer = setTimeout(handleCompare, 500);
            return () => clearTimeout(timer);
        }
    }, [oldText, newText]);

    const handleJumpToLine = (lineNumber: number) => {
        if (!comparison || !lineNumber) return;
        const index = (comparison as any[]).findIndex(line => line.lineNumberB === lineNumber || line.lineNumberA === lineNumber);
        if (index !== -1) {
            setScrollToIndex({ index, timestamp: Date.now() });
        }
    };

    const handleClearAll = () => {
        handleClear();

        setScrollToIndex({ index: null, timestamp: 0 });
    };

    // Handle clicking outside export menu
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
                setShowExportMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleExport = async (format: 'html' | 'pdf') => {
        if (!comparison) return;

        setIsExporting(true);
        try {
            const comparisonResult = {
                comparison,
                matchPercentage,
                diffCount,
                changeSummary: aiSummary || summary
            };

            const result = await apiService.exportReport(
                comparisonResult as any,
                fileName1 || 'Text 1',
                fileName2 || 'Text 2',
                format,
                exportIncludeSummary
            );

            if (format === 'pdf' && result.pdf) {
                const linkSource = `data:application/pdf;base64,${result.pdf}`;
                const downloadLink = document.createElement("a");
                const fileName = `FileFusion_Compare_Report_${new Date().getTime()}.pdf`;
                downloadLink.href = linkSource;
                downloadLink.download = fileName;
                downloadLink.click();
            } else if (format === 'html' && result.html) {
                const blob = new Blob([result.html], { type: 'text/html' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `FileFusion_Compare_Report_${new Date().getTime()}.html`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }
            setShowExportMenu(false);
        } catch (err) {
            console.error("Export error:", err);
            alert("Failed to export report. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <SEO {...seoConfig.textCompare} />
            <ToolPageLayout
                icon={<Split size={36} />}
                title="Text Compare & Diff Tool"
                subtitle="Compare text, code, and files side-by-side with our free online Diff Checker. Secure, private, and AI-powered."
                tags={(
                    <>
                        <SecurityBadge type="secure" />
                        <SecurityBadge type="deleted" />
                        <SecurityBadge type="private" />
                    </>
                )}
            >

                {/* Comparison Results - NOW AT TOP */}
                {comparison && (
                    <div className="block mt-4 mb-12 scroll-mt-[80px]" ref={resultsRef}>
                        <div className="w-full min-w-0">
                            <section className="mt-12 animate-[fadeIn_0.3s_ease-out]" aria-label="Comparison Results">
                                <div className="flex justify-end mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="flex gap-3">
                                            <div className="relative inline-block" ref={exportMenuRef}>
                                                <button
                                                    className={`flex items-center gap-2 py-2 px-4 rounded-lg text-[13.6px] font-semibold cursor-pointer transition-all duration-200 border bg-white ${isExporting ? 'opacity-90 cursor-wait' : 'hover:-translate-y-[1px] hover:bg-blue-50 hover:border-blue-300'} border-blue-200 text-blue-600`}
                                                    onClick={() => setShowExportMenu(!showExportMenu)}
                                                    title="Export Comparison"
                                                    disabled={isExporting}
                                                >
                                                    <Download size={16} className={isExporting ? 'animate-[slow-blink_1.5s_infinite]' : ''} />
                                                    <span>{isExporting ? 'Exporting...' : 'Export'}</span>
                                                </button>

                                                {showExportMenu && (
                                                    <div className="absolute top-[calc(100%+8px)] right-0 w-[240px] bg-white border border-slate-200 rounded-xl shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1),0_8px_10px_-6px_rgba(0,0,0,0.1)] z-[1000] p-2 animate-[slideDown_0.2s_ease-out]">
                                                        <div className="px-3 py-2 text-[12px] font-bold text-slate-500 uppercase tracking-wide">Export Options</div>
                                                        <div className="px-3 py-2">
                                                            <label className="flex justify-between items-center text-[13.6px] text-slate-700 cursor-pointer">
                                                                <span>Include Analysis</span>
                                                                <input
                                                                    type="checkbox"
                                                                    className="cursor-pointer w-4 h-4 accent-indigo-500"
                                                                    checked={exportIncludeSummary}
                                                                    onChange={(e) => setExportIncludeSummary(e.target.checked)}
                                                                />
                                                            </label>
                                                        </div>
                                                        <div className="h-px bg-slate-100 my-1"></div>
                                                        <button
                                                            className="w-full flex items-center justify-start gap-2.5 py-2.5 px-3 border-none bg-transparent rounded-lg text-slate-700 text-[13.6px] font-medium cursor-pointer transition-all duration-200 text-left hover:bg-slate-50 hover:text-indigo-500 group"
                                                            onClick={() => handleExport('html')}
                                                        >
                                                            <FileText size={14} className="text-slate-400 group-hover:text-indigo-500" />
                                                            Download HTML
                                                        </button>
                                                        <button
                                                            className="w-full flex items-center justify-start gap-2.5 py-2.5 px-3 border-none bg-transparent rounded-lg text-slate-700 text-[13.6px] font-medium cursor-pointer transition-all duration-200 text-left hover:bg-slate-50 hover:text-indigo-500 group"
                                                            onClick={() => handleExport('pdf')}
                                                        >
                                                            <FileText size={14} className="text-slate-400 group-hover:text-indigo-500" />
                                                            Download PDF
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                className="flex items-center gap-2 py-2 px-4 rounded-lg text-[13.6px] font-semibold cursor-pointer transition-all duration-200 border bg-white text-rose-600 border-rose-200 hover:-translate-y-[1px] hover:bg-rose-50 hover:border-rose-300"
                                                onClick={handleClearAll}
                                                title="Clear Results"
                                            >
                                                <Trash2 size={16} />
                                                <span>Clear</span>
                                            </button>
                                        </div>

                                        <div>
                                            <button
                                                className={`py-2 px-5 rounded-full text-[13.6px] font-semibold flex items-center gap-2 cursor-pointer transition-all duration-200 ${aiSettings.showAiSummary ? 'bg-white text-indigo-500 border-2 border-indigo-500 py-[6px] px-[18px] shadow-none' : 'bg-gradient-to-br from-indigo-500 to-violet-500 text-white border-none shadow-[0_2px_4px_rgba(99,102,241,0.3)] hover:not(:disabled):-translate-y-[1px] hover:not(:disabled):shadow-[0_4px_12px_rgba(99,102,241,0.4)]'} ${isAiLoading ? 'opacity-90 cursor-wait' : ''}`}
                                                onClick={async () => {
                                                    if (isAiLoading) return;
                                                    const nextShow = !aiSettings.showAiSummary;
                                                    setAiShowSummary(nextShow);
                                                    if (nextShow && !aiSummary) {
                                                        await handleGenerateAiSummary();
                                                    }
                                                }}
                                                disabled={isAiLoading}
                                                title="AI Smart Summary"
                                            >
                                                <Sparkles size={16} className={isAiLoading ? 'animate-[slow-blink_1.5s_infinite]' : ''} />
                                                <span>Deep Analysis</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {aiSettings.showAiSummary && (isAiLoading || aiSummary) && (
                                    <ComparisonSummary
                                        summary={aiSummary || ''}
                                        impactFlags={impactFlags}
                                        isLoading={isAiLoading}
                                        onRegenerate={handleGenerateAiSummary}
                                        onJumpToLine={handleJumpToLine}
                                    />
                                )}

                                <div className="flex justify-center mb-8 w-full">
                                    <ComparisonStats
                                        stats={stats as any}
                                        matchPercentage={matchPercentage}
                                        diffCount={stats?.diffCount || 0}
                                    />
                                </div>

                                <div className="mt-8 flex flex-col gap-3">
                                    <div className="flex items-start">
                                        <div className={`w-[220px] shrink-0 sticky top-[70px] z-[100] mr-4 transition-all duration-[700ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${isCollapsed ? '!w-0 !mr-0' : ''}`}>
                                            <SidebarTools
                                                options={options}
                                                updateOptions={updateOptions}
                                                onTransformText={handleTransformText}
                                                isProcessing={isLoading}
                                                isCollapsed={isCollapsed}
                                                setIsCollapsed={handleManualToggle}
                                                activeTransform={activeTransforms}
                                            />
                                        </div>
                                        <div className={`flex-1 min-w-0 bg-white rounded-xl border border-slate-100 p-0 min-h-[400px] relative shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] overflow-hidden mode-${options.viewType}`}>
                                            <div className={`flex flex-col bg-slate-50 border-b border-slate-100 ${options.viewType === 'split' ? 'md:flex-row' : ''}`}>
                                                <div className={`flex items-center gap-2 px-4 py-2 text-[13.6px] text-slate-500 ${options.viewType === 'split' ? 'flex-1 border-r border-slate-100' : ''}`}>
                                                    <FileText size={14} className="text-indigo-500" />
                                                    <span className="font-semibold text-slate-600">Original:</span>
                                                    <span className="text-slate-800 font-medium whitespace-nowrap overflow-hidden text-ellipsis">{fileName1 || 'Text 1'}</span>
                                                </div>
                                                <div className={`flex items-center gap-2 px-4 py-2 text-[13.6px] text-slate-500 ${options.viewType === 'split' ? 'flex-1' : ''}`}>
                                                    <FileText size={14} className="text-indigo-500" />
                                                    <span className="font-semibold text-slate-600">Changed:</span>
                                                    <span className="text-slate-800 font-medium whitespace-nowrap overflow-hidden text-ellipsis">{fileName2 || 'Text 2'}</span>
                                                </div>
                                            </div>
                                            <DiffView
                                                comparison={comparison as any}
                                                scrollToIndex={scrollToIndex}
                                                viewType={options.viewType as 'split' | 'unified'}
                                                characterLevel={options.characterLevel}
                                                wrapLines={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                )}

                <div className={`flex flex-col transition-all duration-300 ${comparison ? 'mt-20 pt-12 border-t border-slate-100 scroll-mt-[100px]' : ''}`} ref={inputRef}>
                    <InputSection
                        oldText={oldText}
                        newText={newText}
                        setOldText={handleOldTextChange}
                        setNewText={handleNewTextChange}
                        fileName1={fileName1}
                        fileName2={fileName2}
                        setFileName1={setFileName1}
                        setFileName2={setFileName2}
                        onCompare={() => {
                            isManualCompareRef.current = true;
                            handleCompare();
                        }}
                        onFileUpload={handleFileUpload}
                        isLoading={isLoading}
                    />

                    {error && (
                        <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-200 text-rose-700 mx-4 mb-4 rounded-lg text-[14.5px] font-medium leading-[1.5]">
                            <AlertTriangle size={20} />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                {/* Section Navigator - Vertical on the right */}
                {
                    showNavigator && comparison && (
                        <div className="fixed bottom-12 right-2 flex flex-col gap-2 p-1.5 bg-white/95 backdrop-blur-md border border-black/10 rounded-full shadow-[0_12px_32px_rgba(0,0,0,0.1)] z-[1000] opacity-50 transition-opacity duration-300 hover:opacity-100">
                            {(activeSection === 'editors' || activeSection === 'between') && (
                                <button
                                    className="w-9 h-9 p-0 flex items-center justify-center bg-slate-50 border-none rounded-full text-blue-600 cursor-pointer transition-all duration-200 hover:bg-blue-600 hover:text-white hover:scale-110"
                                    onClick={() => scrollToSection(resultsRef)}
                                    title="Jump to Results Summary"
                                >
                                    <ArrowUp size={24} />
                                </button>
                            )}
                            {(activeSection === 'results-top' || activeSection === 'between') && (
                                <button
                                    className="w-9 h-9 p-0 flex items-center justify-center bg-slate-50 border-none rounded-full text-blue-600 cursor-pointer transition-all duration-200 hover:bg-blue-600 hover:text-white hover:scale-110"
                                    onClick={() => scrollToSection(inputRef)}
                                    title="Jump to Input Editors"
                                >
                                    <ArrowDown size={24} />
                                </button>
                            )}
                        </div>
                    )
                }

                {/* SEO & Informational Content Sections */}
                {
                    !comparison && !isLoading && (
                        <>
                            {/* Tool Info Strip */}
                            <ToolInfoStrip>
                                <ToolInfoStrip.Section variant="privacy">
                                    <h3><Shield size={20} />Privacy First</h3>
                                    <p>Your data never leaves your browser. All comparisons are performed locally for maximum security.</p>
                                </ToolInfoStrip.Section>
                                <ToolInfoStrip.Section>
                                    <h3><Zap size={20} />High Precision</h3>
                                    <p>Our algorithm accurately identifies line changes even in heavily shuffled files.</p>
                                </ToolInfoStrip.Section>
                                <ToolInfoStrip.Section>
                                    <h3><Sparkles size={20} />Smart Detect</h3>
                                    <p>Automatically detects moved blocks of code or text to minimize noise in diffs.</p>
                                </ToolInfoStrip.Section>
                            </ToolInfoStrip>

                            {/* How It Works Section */}
                            <ToolSection variant="light" title="How to Use Text Compare" grid="steps">
                                <ToolStepCard number={1} title="Enter Content" description="Paste your original text in the left panel and the modified version in the right panel." />
                                <ToolStepCard number={2} title="Adjust Settings" description="Use the sidebar to toggle real-time comparison, unified view, or ignore case/whitespace." />
                                <ToolStepCard number={3} title="Review Changes" description="Differences are highlighted instantly. Use the navigation buttons to jump between changes." />
                            </ToolSection>

                            {/* Features Section */}
                            <ToolSection variant="muted" title="Why Use Text Compare?" grid="features">
                                <ToolFeatureCard><Zap size={28} /><h3>High-Precision Engine</h3><p>Our Patience Diff algorithm accurately identifies line changes even in heavily shuffled files.</p></ToolFeatureCard>
                                <ToolFeatureCard><Shield size={28} /><h3>Privacy First</h3><p>Your data never leaves your browser. All comparisons are performed locally for maximum security.</p></ToolFeatureCard>
                                <ToolFeatureCard><Sparkles size={28} /><h3>Smart Move Detection</h3><p>Detected moved blocks automatically, even if they've been slightly modified during the move.</p></ToolFeatureCard>
                                <ToolFeatureCard><FileText size={28} /><h3>Multiple Formats</h3><p>Compare plain text, code snippets, JSON, XML, or any other text-based format with ease.</p></ToolFeatureCard>
                            </ToolSection>

                            {/* Use Cases Section */}
                            <ToolSection variant="muted" title="Common Use Cases" grid="features">
                                <ToolFeatureCard><h3>Code Review</h3><p>Compare two versions of a code snippet to identify bug fixes, feature additions, or regressions.</p></ToolFeatureCard>
                                <ToolFeatureCard><h3>Config Checking</h3><p>Spot subtle differences between configuration files (e.g., JSON, YAML) across different environments.</p></ToolFeatureCard>
                                <ToolFeatureCard><h3>Document Editing</h3><p>Track changes in drafts, articles, or legal documents to ensure no unauthorized edits were made.</p></ToolFeatureCard>
                                <ToolFeatureCard><h3>List Comparison</h3><p>Compare two lists of items (e.g., emails, inventory) to find missing or added entries quickly.</p></ToolFeatureCard>
                            </ToolSection>

                            {/* FAQ Section */}
                            <ToolSection variant="light" title="Frequently Asked Questions" grid="none">
                                <div className="flex flex-col gap-[20px] mb-[32px]">
                                    <ToolFeatureCard variant="faq"><h3>Is my data secure?</h3><p>Yes. This tool is built with a "Privacy-First" approach. All processing happens locally in your browser; no text is sent to our servers.</p></ToolFeatureCard>
                                    <ToolFeatureCard variant="faq"><h3>What is "Patience Diff"?</h3><p>Patience Diff is an advanced algorithm that focuses on matching unique lines first, providing much cleaner results for code and long documents than standard algorithms.</p></ToolFeatureCard>
                                    <ToolFeatureCard variant="faq"><h3>Can I ignore whitespace?</h3><p>Yes, you can toggle "Ignore Whitespace" in the settings to focus only on meaningful text changes.</p></ToolFeatureCard>
                                    <ToolFeatureCard variant="faq"><h3>Does it support large files?</h3><p>Yes, the tool is optimized for performance and can handle large text blocks efficiently within your browser's memory limits.</p></ToolFeatureCard>
                                </div>
                                <ToolFaqCta prompt="More questions?" to="/faq" label="Visit our FAQ page" />
                            </ToolSection>

                            {/* Related Tools */}
                            <ToolSection variant="muted" title="Related Tools" grid="related">
                                <ToolRelatedCard to="/tools/json-validator-formatter" icon={<FileText size={24} />} title="JSON Validator" description="Validate and format JSON data" linkLabel="Try Tool" />
                                <ToolRelatedCard to="/tools/file-diff" icon={<Split size={24} />} title="File Diff" description="Compare entire files side-by-side" linkLabel="Try Tool" />
                                <ToolRelatedCard to="/tools/scan-image-to-text" icon={<Search size={24} />} title="OCR Tool" description="Extract text from images" linkLabel="Try Tool" />
                            </ToolSection>
                        </>
                    )
                }
            </ToolPageLayout>
            {/* SEO Content Block (Crawlable) */}
            <SEOContent />

            {/* Footer Margin */}
            <div style={{ height: '4rem' }}></div>
        </>
    );
};

export default TextCompare;
